import styles from './MenuLateral.module.css'
import ItemMenu from './ItemMenu';
import { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Context } from '../../../context/UsuarioContext';
import { SECOES, GRUPOS, podeVer } from '../../../utils/secoes';

const subItensPorChave = {
  empresas: [
    { descricao: "Consultar Empresas", icone: "bi bi-search", rota: "/empresas" },
    { descricao: "Adicionar Empresa", icone: "bi bi-plus", rota: "/cadastro-empresa" },
  ],
  emprestimos: [
    { descricao: "Consultar Empréstimos", icone: "bi bi-search", rota: "/emprestimos" },
    { descricao: "Novo Empréstimo", icone: "bi bi-plus", rota: "/cadastro-emprestimo" },
  ],
};

export default function MenuLateral() {
  const { usuario } = useContext(Context);
  const [aberto, setAberto] = useState(false);
  const [gruposAbertos, setGruposAbertos] = useState(() => {
    try { return JSON.parse(localStorage.getItem("menu-grupos-abertos")) || {}; } catch { return {}; }
  });
  const [escondidas, setEscondidas] = useState(() => {
    try { return JSON.parse(localStorage.getItem("menu-secoes-escondidas")) || []; } catch { return []; }
  });
  const [editando, setEditando] = useState(false);
  const location = useLocation();
  const carregado = !!usuario?.id;

  // Fecha a gaveta ao trocar de rota (tocou num item)
  useEffect(() => { setAberto(false); }, [location.pathname]);

  function toggleGrupo(chave) {
    setGruposAbertos((cur) => {
      // padrão aberto (igual ao render) — senão o 1º clique pra recolher não fazia nada
      const novo = { ...cur, [chave]: !(cur[chave] ?? true) };
      try { localStorage.setItem("menu-grupos-abertos", JSON.stringify(novo)); } catch {}
      return novo;
    });
  }

  function toggleEsconder(chave) {
    setEscondidas((cur) => {
      const novo = cur.includes(chave) ? cur.filter((c) => c !== chave) : [...cur, chave];
      try { localStorage.setItem("menu-secoes-escondidas", JSON.stringify(novo)); } catch {}
      return novo;
    });
  }

  // Enquanto o usuário não carregou, menu vazio — nunca piscar itens proibidos
  const itens = carregado
    ? SECOES.filter((s) => podeVer(usuario, s.chave)).map((s) => ({
        chave: s.chave,
        icone: s.icone,
        descricao: s.rotulo,
        rota: s.rota,
        grupo: s.grupo,
        subItens: subItensPorChave[s.chave],
      }))
    : [];

  if (carregado && usuario.admin) {
    itens.push({ chave: "equipe", icone: "bi bi-people", descricao: "Equipe", rota: "/equipe", grupo: "gestao" });
  }

  // Agrupa os itens em "caixas" (Carteira, Financeiro, ...), na ordem de GRUPOS,
  // já sem os grupos que ficaram vazios pra esse usuário.
  const gruposVisiveis = GRUPOS
    .map((g) => ({ ...g, itens: itens.filter((i) => i.grupo === g.chave) }))
    .filter((g) => g.itens.length > 0);

  // Uma seção do menu: no modo normal é o link; no modo Personalizar vira uma
  // linha com o olhinho pra esconder/mostrar (sem navegar).
  function renderItem(i, index) {
    if (editando) {
      const oculta = escondidas.includes(i.chave);
      return (
        <li key={index} className={styles.linha_editar}>
          <span className={`${styles.rotulo_editar} ${oculta ? styles.rotulo_oculto : ""}`}>
            <i className={i.icone} style={{ marginRight: 12 }}></i>{i.descricao}
          </span>
          <button type="button" className={styles.olho} onClick={() => toggleEsconder(i.chave)}
                  aria-label={oculta ? "Mostrar seção" : "Esconder seção"} title={oculta ? "Mostrar" : "Esconder"}>
            <i className={oculta ? "bi bi-eye-slash" : "bi bi-eye-fill"}></i>
          </button>
        </li>
      );
    }
    return <ItemMenu key={index} item={i}/>;
  }

  const renderGrupos = () => {
    const grupos = gruposVisiveis.map((g) => {
      // no modo normal, esconde as seções desligadas; no editar, mostra todas
      const itensGrupo = editando ? g.itens : g.itens.filter((i) => !escondidas.includes(i.chave));
      if (itensGrupo.length === 0) return null; // grupo sem nada visível some

      // grupo sem título (Dashboard) fica sempre visível, sem gaveta
      if (!g.rotulo) {
        return (
          <li key={g.chave} className={styles.grupo}>
            <ul className={styles.grupo_lista}>{itensGrupo.map(renderItem)}</ul>
          </li>
        );
      }
      // grupos ABERTOS por padrão (menu cheio, bom no desktop); você recolhe a
      // gaveta clicando no título e isso fica lembrado. No modo editar, tudo aberto.
      const abertoGrupo = editando || (gruposAbertos[g.chave] ?? true);
      return (
        <li key={g.chave} className={styles.grupo}>
          <button type="button" className={styles.grupo_titulo} onClick={() => !editando && toggleGrupo(g.chave)} aria-expanded={abertoGrupo}>
            <span>{g.rotulo}</span>
            {!editando && <i className={`bi bi-chevron-${abertoGrupo ? "up" : "down"}`}></i>}
          </button>
          {abertoGrupo && <ul className={styles.grupo_lista}>{itensGrupo.map(renderItem)}</ul>}
        </li>
      );
    });

    // botão pra ligar/desligar o modo Personalizar (esconder seções)
    grupos.push(
      <li key="__personalizar" className={styles.grupo}>
        <button type="button" className={styles.personalizar} onClick={() => setEditando((v) => !v)}>
          <i className={editando ? "bi bi-check-lg" : "bi bi-sliders"}></i>
          {editando ? " Concluir" : " Personalizar menu"}
        </button>
        {editando && <div className={styles.dica_editar}>Toque no olho pra esconder ou mostrar cada seção.</div>}
      </li>
    );
    return grupos;
  };

  return (
    <>
    {/* Desktop: menu lateral fixo */}
    <div className={`${styles.menu_lateral} nao-imprimir`}>
      <ul className={styles.lista}>
        {renderGrupos()}
      </ul>
    </div>

    {/* Mobile: barra com botão hambúrguer */}
    <div className={`${styles.barra_mobile} nao-imprimir`}>
      <button className={styles.botao_hamburguer} onClick={() => setAberto(true)} aria-label="Abrir menu">
        <i className="bi bi-three-dots-vertical"></i>
      </button>
    </div>

    {/* Mobile: fundo escuro + gaveta deslizante */}
    <div
      className={`${styles.overlay} ${aberto ? styles.overlay_aberto : ""} nao-imprimir`}
      onClick={() => setAberto(false)}
    ></div>
    <aside className={`${styles.gaveta} ${aberto ? styles.gaveta_aberta : ""} nao-imprimir`}>
      <div className={styles.gaveta_cabecalho}>
        <span><i className="bi bi-list" style={{marginRight: 8}}></i>Menu</span>
        <button className={styles.fechar} onClick={() => setAberto(false)} aria-label="Fechar menu">
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      {/* Não fecha no clique do <ul>: itens com submenu (Empresas/Empréstimos) só
          expandem. A gaveta fecha sozinha na troca de rota (useEffect acima). */}
      <ul className={styles.lista}>
        {renderGrupos()}
      </ul>
    </aside>
    </>
  );
}
